<template>
    <span class="edit-countdown" :class="statusClass">
        <q-icon :name="editExpired ? 'lock_clock' : 'schedule'" size="14px" />
        <span v-if="!editExpired" class="countdown-text">{{ remainingTime }}</span>
        <span v-else class="countdown-text">Edit window closed</span>
    </span>
</template>

<script>
export default {
    name: 'EditCountdown',

    props: {
        // Record ka createdWhen / submittedAt timestamp
        createdWhen: {
            type: [String, Date],
            required: true,
        },
        // Server ka current date/time (mismatched local clocks se bachne ke liye)
        serverDate: {
            type: [String, Date],
            required: true,
        },
        // Kitne ghanton ka edit window hai (default 24)
        limitHours: {
            type: Number,
            default: 24,
        },
    },

    emits: ['expired'],

    data() {
        return {
            remainingTime: '',
            remainingSeconds: 0,
            editExpired: false,
            timerInterval: null,
        };
    },

    computed: {
        statusClass() {
            if (this.editExpired) return 'is-expired';
            if (this.remainingSeconds <= 60 * 60) return 'is-critical'; // <1hr
            if (this.remainingSeconds <= this.limitHours * 60 * 60 * 0.25) return 'is-warning'; // last 25%
            return 'is-safe';
        },
    },

    watch: {
        createdWhen() {
            this.startEditCountdown();
        },
        serverDate() {
            this.startEditCountdown();
        },
    },

    mounted() {
        this.startEditCountdown();
    },

    beforeUnmount() {
        if (this.timerInterval) clearInterval(this.timerInterval);
    },
    // Vue 2 compat
    beforeDestroy() {
        if (this.timerInterval) clearInterval(this.timerInterval);
    },

    methods: {
        // "2024-01-15 10:30:00" jesi space-wali (non-ISO) date strings kuch
        // browsers (Safari) mein Invalid Date ban jati hain. Space ko "T"
        // se replace kar dete hain taake ISO-jesi ban jaye aur reliably parse ho.
        toTimestamp(value) {
            if (!value) return NaN;
            if (value instanceof Date) return value.getTime();

            let str = String(value).trim();
            if (str.includes(' ') && !str.includes('T')) {
                str = str.replace(' ', 'T');
            }

            // Backend kabhi kabhi 7-digit fractional seconds bhejta hai
            // (e.g. ".8206336") jise kuch browsers (Firefox/Safari) parse
            // nahi kar pate. Milliseconds tak (3 digits) trim kar dete hain.
            str = str.replace(/(\.\d{3})\d+/, '$1');

            const ts = new Date(str).getTime();
            return ts;
        },

        startEditCountdown() {
            if (this.timerInterval) clearInterval(this.timerInterval);

            const submittedAt = this.toTimestamp(this.createdWhen);
            console.log('submittedAt: ', submittedAt);
            const serverNow = this.toTimestamp(this.serverDate);
            console.log('serverNow: ', serverNow);

            // Zaroori values abhi tak load/valid nahi huin (e.g. serverDate
            // Vuex se async aata hai) — chup chap ruk jao, watcher dobara
            // call karega jab valid value aa jayegi.
            if (Number.isNaN(submittedAt) || Number.isNaN(serverNow)) {
                this.remainingTime = '--:--:--';
                this.editExpired = false;
                // eslint-disable-next-line no-console
                console.warn(
                    '[EditCountdown] Could not parse dates — check the raw values being passed in:',
                    { createdWhen: this.createdWhen, serverDate: this.serverDate }
                );
                return;
            }

            const offset = serverNow - Date.now();
            const limitMs = this.limitHours * 60 * 60 * 1000;

            const tick = () => {
                const now = Date.now() + offset;
                const remainingMs = limitMs - (now - submittedAt);

                if (remainingMs <= 0) {
                    this.remainingSeconds = 0;
                    this.remainingTime = '00:00:00';
                    if (!this.editExpired) {
                        this.editExpired = true;
                        this.$emit('expired');
                    }
                    clearInterval(this.timerInterval);
                    return;
                }

                this.editExpired = false;
                this.remainingSeconds = Math.floor(remainingMs / 1000);

                const hrs = Math.floor(remainingMs / (1000 * 60 * 60));
                const mins = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
                const secs = Math.floor((remainingMs % (1000 * 60)) / 1000);

                this.remainingTime = [hrs, mins, secs].map((v) => String(v).padStart(2, '0')).join(':');
            };

            tick();
            this.timerInterval = setInterval(tick, 1000);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../styles/quasar.scss';

.edit-countdown {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    transition: background 0.2s ease, color 0.2s ease; 
    position: absolute;
    right: 10%;
    top: 190px;
    z-index: initial !important;
}

@media (max-width: 1024px) {
    .edit-countdown {
        top: 130px;
          right: 2%;

    }
}



.countdown-text {
    line-height: 1;
}

/* > 25% time left */
.is-safe {
    color: #2f8a4a;
    background: #e6f5ea;
}

/* last 25% of window */
.is-warning {
    color: #b4790f;
    background: #fdf1de;
}

/* last hour */
.is-critical {
    color: #c45151;
    background: #fceeee;
    animation: pulse-critical 1.4s ease-in-out infinite;
}

/* window closed */
.is-expired {
    color: #87969a;
    background: #eef1f2;
}


@keyframes pulse-critical {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.6;
    }
}
</style>